import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AWAKENING, BUFF_GROUPS, CHARACTERS_PER_PAGE, RARITY, ROLES, ROLE_MAP, SKILLS, TAG, UNIT_GROUPS } from '../config';

import { usePerservedState } from '../utils/versionDataUtil';

import { ReactComponent as DropArrow } from '../assets/svgs/drop-down-arrow.svg';
import { ReactComponent as RefreshIcon } from '../assets/svgs/refresh.svg';

import redElement from '../assets/images/colours/red_w.webp';
import greenElement from '../assets/images/colours/green_w.webp';
import yellowElement from '../assets/images/colours/yellow_w.webp';
import blueElement from '../assets/images/colours/blue_w.webp';

import GroupGrid from '../components/GroupGrid';
import PageControls from '../components/PageControls';
import SearchBar from '../components/SearchBar';
import ScrollArrow from '../components/ScrollArrow';
import Skill from '../components/Skill';
import './pages.css';

function checkBuffMatches(character, propertyName, subPropertyName, filterState) {
    const property = character[propertyName];
    const subProperty = property[subPropertyName];
    const searchText = filterState.searchText.toLowerCase();
    if (!property || !property[`${subPropertyName}_buffs`]) {
        return false;
    }

    const buffs = property[`${subPropertyName}_buffs`];

    if (!filterState.searchText && filterState.buffs.length === 0) {
        property[`${subPropertyName}_match`] = Array.isArray(subProperty) ? new Array(Object.keys(buffs).length).fill(true) : true;
        return true;
    }


    let textMatch = false;
    if (Array.isArray(property[subPropertyName])) {
        textMatch = property[subPropertyName].map(item => item.toLowerCase().includes(searchText));
    } else {
        textMatch = property[subPropertyName].toLowerCase().includes(searchText);
    }

    if (!textMatch) {
        property[`${subPropertyName}_match`] = Array.isArray(property[subPropertyName]) ? new Array(Object.keys(buffs).length).fill(false) : false;
        return false;
    }

    if (filterState.buffs.length === 0) {
        property[`${subPropertyName}_match`] = textMatch;
        return Array.isArray(textMatch) ? textMatch.some(match => match) : textMatch;
    }


    let matchResult;
    if (Array.isArray(subProperty)) {
        matchResult = Object.values(buffs).map(buffGroup => {
            return filterState.any
                ? buffGroup.some(buff => filterState.buffs.includes(`${buff.buff}_${buff.type}`))
                : filterState.buffs.every(filterBuff => {
                    const [filterBuffName, filterBuffType] = filterBuff.split("_");
                    return buffGroup.some(buffInGroup => buffInGroup.buff === filterBuffName && buffInGroup.type === filterBuffType);
                });
        });

        property[`${subPropertyName}_match`] = matchResult.map((groupMatch, index) => {
            return groupMatch && (Array.isArray(textMatch) ? textMatch[index] : textMatch);
        });
    } else {
        matchResult = filterState.any
            ? buffs.some(buff => filterState.buffs.includes(`${buff.buff}_${buff.type}`))
            : filterState.buffs.every(filterBuff => {
                const [filterBuffName, filterBuffType] = filterBuff.split("_");
                return buffs.some(buffInGroup => buffInGroup.buff === filterBuffName && buffInGroup.type === filterBuffType);
            });

        property[`${subPropertyName}_match`] = matchResult && textMatch;
    }

    return Array.isArray(property[`${subPropertyName}_match`]) ? property[`${subPropertyName}_match`].some(match => match) : property[`${subPropertyName}_match`];
}

function renderSkill(character, propertyName, propertyKey, subPropertyName, title, buffData) {
    const property = character[propertyName];
    const isAX = subPropertyName === 'ax';
    const subPropertySkill = `${subPropertyName}_skills`;
    const isCN = ['c', 'n'].includes(propertyKey);

    let imageKey = property.icon;
    if (isAX && propertyKey !== 'n') {
        imageKey = Array.isArray(imageKey)
            ? imageKey.map(ik => `${ik}_a`)
            : `${imageKey}_a`;
    } else if (Array.isArray(imageKey)) {
        imageKey = [...imageKey];
    } else if (propertyKey === 'c') {
        imageKey = character.role;
    } else if (propertyKey === 'n') {
        imageKey = isAX ? '1_a' : '1';
    }

    const name = propertyKey === 's' ? character.title : property.name;

    if (!Array.isArray(property[subPropertyName])) {
        if (!property[`${subPropertyName}_match`]) return;

        const imageSrc = propertyKey === 'g' ? property.image : SKILLS[imageKey];
        const titleContent = isAX
            ? `${propertyKey !== 'n' ? `${title}: ${name} +` : `${title} +`}`
            : `${title}${isCN ? '' : `: ${name}`}`;

        return (
            <div className="skill-sub-container">
                <div className="skill-sub-title-container large">
                    <div className="skill-image-container large">
                        <img src={imageSrc} alt={name} className="skill-image large" />
                        {isAX ? (
                            <img
                                className="awakening-image-small"
                                src={AWAKENING[`a${character.order.indexOf(propertyKey) + 1}`]}
                                alt={`${propertyKey} Awakening`}
                            />
                        ) : property["ss+"] && (
                            <img className="ssplus-image small" src={AWAKENING["ss+"]} alt="" />
                        )}
                    </div>
                    {titleContent}
                </div>
                <Skill skill={property[subPropertySkill]} buffData={buffData} dark={true} />
            </div>
        );
    }

    return [0, 1].map(i => {
        if (!property[`${subPropertyName}_match`][i]) return null;

        const currentImageKey = Array.isArray(imageKey) ? imageKey[i] : imageKey;
        const currentName = Array.isArray(name) ? name[i] : name;
        const tagSrc = character.groups.includes("Tag")
            ? i === 0 ? TAG.tag_l : TAG.tag_r
            : i === 0 ? TAG.tag_1 : TAG.tag_2;

        return (
            <div className="skill-sub-container" key={i}>
                <div className="skill-sub-title-container large">
                    <div className="skill-image-container large">
                        <img
                            src={SKILLS[currentImageKey]}
                            alt={property.name}
                            className="skill-image large"
                        />
                        {isAX && (
                            <img
                                className="awakening-image-small"
                                src={AWAKENING[`a${character.order.indexOf(propertyKey) + 1}`]}
                                alt={`${propertyKey} Awakening`}
                            />
                        )}
                    </div>
                    {isAX
                        ? `${title}: ${currentName} +`
                        : `${title}${isCN ? '' : `: ${currentName}`}`}
                    <img className="tag-icon small" src={tagSrc} alt="" />
                </div>
                <Skill skill={property[subPropertySkill][i]} buffData={buffData} />
            </div>
        );
    });
}

const characterTextMatch = (character, filterState, checkedKeys = []) => {
    const searchText = filterState.searchText.toLowerCase();

    if (!searchText) {
        return true;
    }

    const checkObject = (obj) => {
        if (typeof obj === 'string') {
            return obj.toLowerCase().includes(searchText);
        }

        if (Array.isArray(obj)) {
            return obj.some(item => checkObject(item));
        }

        if (typeof obj === 'object' && obj !== null) {
            const keys = Object.keys(obj);
            for (const key of keys) {
                if (checkedKeys.length > 0 && !checkedKeys.includes(key)) {
                    continue;
                }
                if (checkObject(obj[key])) {
                    return true;
                }
            }
            return false;
        }

        return false;
    };

    return Object.keys(character).some(key => {
        if (checkedKeys.length > 0 && !checkedKeys.includes(key)) {
            return false;
        }
        return checkObject(character[key]);
    });
};

const colours = [
    { code: 'r', className: 'element-red', image: redElement, alt: 'Red' },
    { code: 'g', className: 'element-green', image: greenElement, alt: 'Green' },
    { code: 'y', className: 'element-yellow', image: yellowElement, alt: 'Yellow' },
    { code: 'b', className: 'element-blue', image: blueElement, alt: 'Blue' },
];

const roles = [
    { code: 't', image: ROLES.tank, alt: 'Tank' },
    { code: 'a', image: ROLES.dps, alt: 'Offense' },
    { code: 'h', image: ROLES.heal, alt: 'Support' },
];

const rarities = [
    { code: 'n', image: RARITY.rarity_n, alt: 'Standard' },
    { code: 'l', image: RARITY.rarity_l, alt: 'Limited' },
    { code: 's', image: RARITY.rarity_s, alt: 'SF' },
];

const filterOptions = [
    { value: 'hidden', label: 'Hidden Technique' },
    { value: 'skill1', label: 'Skill 1' },
    { value: 'skill2', label: 'Skill 2' },
    { value: 'special', label: 'Special Ability' },
    { value: 'normal', label: 'Normal Attack' },
    { value: 'combo', label: 'Combo' },
    { value: 'gear1', label: 'Gear 1' },
    { value: 'gear2', label: 'Gear 2' },

];

const skills = [
    { name: "hidden", awakening: "a0", key: "h", "title": "Hidden Technique" },
    { name: "hidden", awakening: "ax", key: "h", "title": "Hidden Technique" },
    { name: "skill1", awakening: "a0", key: "1", "title": "Skill 1" },
    { name: "skill1", awakening: "ax", key: "1", "title": "Skill 1" },
    { name: "skill2", awakening: "a0", key: "2", "title": "Skill 2" },
    { name: "skill2", awakening: "ax", key: "2", "title": "Skill 2" },
    { name: "special", awakening: "a0", key: "s", "title": "Special Ability" },
    { name: "special", awakening: "ax", key: "s", "title": "Special Ability" },
    { name: "normal", awakening: "a0", key: "n", "title": "Normal Attack" },
    { name: "normal", awakening: "ax", key: "n", "title": "Normal Attack" },
    { name: "combo", awakening: "a0", key: "c", "title": "Combo" },
    { name: "gear1", awakening: "a0", key: "g", "title": "Gear 1" },
    { name: "gear2", awakening: "a0", key: "g", "title": "Gear 2" },

];

function SkillsPage({ characterData, buffData }) {
    const intl = useIntl();
    const navigate = useNavigate();
    const filterRef = useRef(null);
    const groupContainerRef = useRef(null);

    const [modifiedCharacterData, setModifiedCharacterData] = useState(new Map());
    const [filteredCharacters, setFilteredCharacters] = useState({});
    const defaultFilterState = {
        searchText: '',
        colours: ['r', 'g', 'y', 'b'],
        roles: ['t', 'a', 'h'],
        rarity: ['n', 'l', 's'],
        awakening: { "a0": true, "ax": true },
        buffs: [],
        groups: [],
        filterBy: [],
        any: true
    };

    const [filterState, setFilterState] = usePerservedState('skillsPageState', defaultFilterState)
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);

    useEffect(() => {
        const propertiesToCheck = ['hidden', 'skill1', 'skill2', 'special', 'combo', 'normal'];

        const updatedCharacterDataWithMatches = Object.values(characterData).map(character => {
            const updatedCharacter = {
                ...character,
                gear1: { ...character.gear1, match: false },
                ...(character.gear2 ? { gear2: { ...character.gear2, match: false } } : {}),
            };

            propertiesToCheck.forEach(propName => {
                if (updatedCharacter[propName]) {
                    updatedCharacter[propName] = {
                        ...updatedCharacter[propName],
                        a0_match: Array.isArray(updatedCharacter[propName].a0) ? [false, false] : false,
                        ...(updatedCharacter[propName].ax ? { ax_match: Array.isArray(updatedCharacter[propName].ax) ? [false, false] : false } : {}),
                    };
                }
            });

            return updatedCharacter;
        });

        setModifiedCharacterData(updatedCharacterDataWithMatches);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOpen]);

    useEffect(() => {
        if (modifiedCharacterData && Object.keys(modifiedCharacterData).length > 0) {
            let filteredAndSorted = applyFilters(modifiedCharacterData)
            setPage(1);
            setFilteredCharacters(filteredAndSorted);
        } else {
            setFilteredCharacters([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterState, modifiedCharacterData]);


    const resetFilters = () => {
        setFilterState(defaultFilterState);
    }

    const handleAwakeningClick = (awakening) => {
        setFilterState((prevStates) => ({
            ...prevStates,
            "awakening": { ...prevStates["awakening"], [awakening]: !filterState["awakening"][awakening] },
        }));
    };

    const handleCharacterClick = (characterKey) => {
        navigate(`/character/${characterKey}`);
    };

    const handleFilterClick = (filterType, value) => {
        setFilterState(prevState => {
            const currentValues = prevState[filterType];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            return { ...prevState, [filterType]: newValues };
        });
    };

    const handleFilterChange = (value) => {
        setFilterState(prevState => ({
            ...prevState,
            filterBy: prevState.filterBy.includes(value)
                ? prevState.filterBy.filter(filter => filter !== value)
                : [...prevState.filterBy, value],
        }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= Object.keys(filteredCharacters).length) {
            setPage(newPage);
            window.scrollTo({
                top: 0,
            });
        }
    };

    const handleSearchChange = (value) => {
        const newSearchText = value;
        setFilterState(prevState => {
            if (prevState.searchText !== newSearchText) {
                return { ...prevState, searchText: newSearchText };
            }
            return prevState;
        });
    };

    const toggleAny = (e) => {
        const value = e.target.value === 'true';
        setFilterState(prevState => ({
            ...prevState,
            any: value,
        }));
    };

    const applyFilters = (data) => {
        if (!data) return [];

        const { searchText, colours, roles, rarity, groups } = filterState;
        const lowerSearchText = searchText.toLowerCase();

        return Object.values(data).filter(character => {
            const lowerName = (character.name ?? '').toLowerCase();
            const lowerTitle = (character.title ?? '').toLowerCase();
            const lowerColour = (character.colour ?? '').toLowerCase();
            const lowerRole = (character.role ?? '').toLowerCase();
            const lowerRarity = (character.rarity ?? '').toLowerCase();

            const baseFiltersMatch = (lowerName.includes(lowerSearchText) || lowerTitle.includes(lowerSearchText) || characterTextMatch(character, filterState, ["hidden", "name", "skill1", "skill2", "a0", "ax", "special", "normal", "combo", "gear1", "gear2"])) &&
                colours.includes(lowerColour) &&
                roles.includes(lowerRole) &&
                rarity.includes(lowerRarity) &&
                checkGroupMatch(character, groups);

            if (!baseFiltersMatch) {
                return false;
            }

            const abilityMatches = new Map();

            for (const { name, awakening } of skills) {
                if (character.hasOwnProperty(name)) {
                    abilityMatches.set(`${name}_${awakening}`, checkBuffMatches(character, name, awakening, filterState));
                }
            }

            let anyMatch = false;
            anyMatch = Array.from(abilityMatches.entries()).some(([key, match]) => {
                const [name, awakening] = key.split("_");
                return (filterState.filterBy.length === 0 || filterState.filterBy.includes(name)) &&
                    match &&
                    (['gear1', 'gear2', 'combo'].includes(name) ? true : filterState.awakening[awakening]);
            });


            return anyMatch;
        });
    };

    const checkGroupMatch = (character, groups) => {
        if (groups.length === 0) return true;

        const characterGroupsToCheck = (character.groups ?? [])
            .map(charGroup => UNIT_GROUPS.includes(charGroup) ? charGroup : "Others");

        return groups.every(filterGroup => {
            if (UNIT_GROUPS.includes(filterGroup)) {
                return characterGroupsToCheck.includes(filterGroup);
            } else if (filterGroup === "Others") {
                return characterGroupsToCheck.some(charGroup => !UNIT_GROUPS.includes(charGroup));
            }
            return false;
        });
    };

    if (Object.keys(modifiedCharacterData).length === 0 || Object.keys(buffData).length === 0) {
        return (
            <div className="page">
                <div className="main-container">
                    <div className="surround-margin"></div>
                    <div className="sub-container">
                        <h1 className="main-header">
                            <FormattedMessage
                                id="main.characters"
                                defaultMessage="Characters"
                            />
                            <RefreshIcon className="refresh-icon" />
                        </h1>
                        <div className="main-filter-container">
                            <input
                                type="search"
                                placeholder={intl.formatMessage({ id: 'main.search', defaultMessage: 'Search' })}
                                className="search-bar"
                                value=""
                                readOnly
                            />
                            <div className="filter-container">
                                {colours.map(colour => (
                                    <div
                                        key={colour.code}
                                        className={`filter-colour ${colour.className} ${filterState.colours.includes(colour.code) ? 'active' : ''}`}
                                    >
                                        <img className={colour.className === 'element-green' ? 'element-green-image' : 'element-image'} src={colour.image} alt={colour.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="filter-container">
                                {roles.map(role => (
                                    <div
                                        key={role.code}
                                        className={`role-filter ${filterState.roles.includes(role.code) ? 'active' : ''}`}
                                    >
                                        <img className="role-image" src={role.image} alt={role.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="filter-container">
                                {rarities.map(rarity => (
                                    <div
                                        key={rarity.code}
                                        className={`role-filter ${filterState.rarity.includes(rarity.code) ? 'active' : ''}`}
                                    >
                                        <img className="ticket-image" src={rarity.image} alt={rarity.alt} />
                                    </div>
                                ))}
                            </div>
                            <div className="group-container">
                                <div className="buff-title-container">
                                    <div>
                                        <FormattedMessage
                                            id="sort.groups"
                                            defaultMessage="Groups"
                                        />
                                    </div>
                                    <div className="filter-bubble">
                                        All
                                    </div>
                                </div>
                            </div>
                            {Object.entries(BUFF_GROUPS).map(([key, group]) => (
                                <div key={key} className="filter-container buff">
                                    {group.buffs.map((buff, buffIndex) => {
                                        return (
                                            <div
                                                className="role-filter"
                                                key={`${key}-${buffIndex}`}
                                            >
                                                <div className="image-container">
                                                    <div className="loading-image icon" />
                                                </div>
                                            </div>

                                        );
                                    })}
                                </div>
                            ))}
                            <div>
                                <select
                                    className="sort-select"
                                >
                                    <option>
                                        Any
                                    </option>
                                </select>
                            </div>
                            <div className="sort-container">
                                <div className="sort-select-container">
                                    <select className="sort-select" value="Default" readOnly>
                                        <option>
                                            Filter
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div>
                            {Array.from({ length: 7 }).map((_, index) => (
                                <div key={index}>

                                    <div className="image-container">
                                        <div className="loading-image medium" />
                                    </div>
                                    <div className="gear-item">
                                        <div className="loading-image long" />
                                    </div>
                                    <br />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="surround-margin"></div>
                </div>
            </div >
        );
    }

    return (
        <div className="page">
            <div className="main-container">
                <div className="surround-margin"></div>
                <div className="sub-container">
                    <h1 className="main-header">
                        <FormattedMessage
                            id="main.skills"
                            defaultMessage="Skills"
                        />
                        <RefreshIcon onClick={() => resetFilters()} className="refresh-icon" />
                    </h1>
                    <div className="main-filter-container">
                        <SearchBar filterState={filterState} handleSearchChange={handleSearchChange} />
                        <div className="filter-container">
                            {colours.map(colour => (
                                <div
                                    key={colour.code}
                                    className={`filter-colour ${colour.className} ${filterState.colours.includes(colour.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('colours', colour.code)}
                                >
                                    <img className={colour.className === 'element-green' ? 'element-green-image' : 'element-image'} src={colour.image} alt={colour.alt} />
                                </div>
                            ))}
                        </div>
                        <div className="filter-container">
                            {roles.map(role => (
                                <div
                                    key={role.code}
                                    className={`role-filter ${filterState.roles.includes(role.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('roles', role.code)}
                                >
                                    <img className="role-image" src={role.image} alt={role.alt} />
                                </div>
                            ))}
                        </div>
                        <div className="filter-container">
                            {rarities.map(rarity => (
                                <div
                                    key={rarity.code}
                                    title={rarity.alt}
                                    className={`role-filter ${filterState.rarity.includes(rarity.code) ? 'active' : ''}`}
                                    onClick={() => handleFilterClick('rarity', rarity.code)}
                                >
                                    <img className="ticket-image" src={rarity.image} alt={rarity.alt} />
                                </div>
                            ))}
                        </div>
                        <div className="filter-container">
                            <div
                                className={`role-filter ${filterState.awakening.a0 ? 'active' : ''}`}
                                onClick={() => handleAwakeningClick('a0')}
                            >
                                <img
                                    className="gear-group-image" src={AWAKENING.a0} alt="SS Gear" />
                            </div>
                            <div
                                className={`role-filter ${filterState.awakening.ax ? 'active' : ''}`}
                                onClick={() => handleAwakeningClick('ax')}
                            >
                                <img
                                    className="gear-group-image" src={AWAKENING.a5} alt="SS+ Gear" />
                            </div>
                        </div>
                        <GroupGrid filterState={filterState} handleFilterClick={handleFilterClick} groupContainerRef={groupContainerRef} />
                        {Object.entries(BUFF_GROUPS).map(([key, group]) => (
                            <div key={key} className="filter-container buff">
                                {group.buffs.map((buff, buffIndex) => {
                                    const data = buffData[buff];
                                    const buffKey = `${buff}_${group.type}`;
                                    const iconClass = (data.type.includes("Status Ailment") && group.type === 'd') || data.name === "Invincibility" ? 'ticket-image small' : 'ticket-image';
                                    return (
                                        <div
                                            className={`role-filter ${filterState.buffs.includes(buffKey) ? 'active' : ''}`}
                                            onClick={() => handleFilterClick('buffs', buffKey)}
                                            key={`${key}-${buffIndex}`}
                                        >
                                            <img
                                                title={data.name}
                                                className={iconClass}
                                                src={data.images[group.type]}
                                                alt={buffKey} />
                                        </div>

                                    );
                                })}
                            </div>
                        ))}
                        <div>
                            <select
                                className="sort-select"
                                onChange={toggleAny}
                                value={filterState.any}
                            >
                                <option title={intl.formatMessage({ id: 'sort.any.desc', defaultMessage: 'Any buff much match' })} value={"true"}>
                                    <FormattedMessage
                                        id="sort.any"
                                        defaultMessage="Any"
                                    />
                                </option>
                                <option title={intl.formatMessage({ id: 'sort.all.desc', defaultMessage: 'All buffs must match' })} value={"false"}>
                                    <FormattedMessage
                                        id="sort.all"
                                        defaultMessage="All"
                                    />
                                </option>
                            </select>
                        </div>
                        <div ref={filterRef} className="buff-filter-container">
                            <div onClick={() => setFilterOpen(!filterOpen)} className="buff-title-container">
                                <div>
                                    <FormattedMessage
                                        id="filter.filter"
                                        defaultMessage="Filter"
                                    />
                                </div>
                                <div className={`filter-bubble ${filterState.filterBy.length === 0 ? 'hidden' : 'small'}`}>
                                    {filterState.filterBy.length}
                                </div>
                                <DropArrow className="drop-down-arrow"></DropArrow>
                            </div>
                            <div className="sort-container">
                                {filterOpen && (
                                    <div className="buff-filter-content">
                                        <div className="filter-select-container">
                                            {filterOptions.map((option) => (
                                                <label className="filter-option" key={option.value}>
                                                    <input
                                                        type="checkbox"
                                                        value={option.value}
                                                        checked={filterState.filterBy.includes(option.value)}
                                                        onChange={() => handleFilterChange(option.value)}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                )}
                            </div>
                        </div>

                    </div>
                    <div className="skill-grid">
                        {Object.entries(filteredCharacters)
                            .slice((page - 1) * CHARACTERS_PER_PAGE, page * CHARACTERS_PER_PAGE)
                            .map(([characterKey, character]) => (
                                <div key={characterKey}>
                                    <div className="skill-main-container">
                                        <div className="skill-title-container large">
                                            <div className="image-container">
                                                <div className="image-zoom">
                                                    <img
                                                        loading="lazy"
                                                        src={character.images?.m}
                                                        alt={character.name}
                                                        onClick={() => handleCharacterClick(character.id)}
                                                        className="character-image skills"
                                                    />
                                                </div>
                                            </div>
                                            <div className='title-role-skill-page'>
                                                <div className="character-name-title large">
                                                    <p>{`${character.name}: ${character.title}`}</p>
                                                </div>
                                                <img
                                                    className="character-page-role small"
                                                    alt="Role"
                                                    src={ROLES[`${ROLE_MAP[character.role]}_${character.colour}`]}
                                                />
                                            </div>
                                        </div>
                                        <div className="skill-container">
                                            {Object.entries(skills).map(([key, skill]) => {
                                                const customKey = `${character.id}_${key}_${skill.name}`;
                                                const shouldRenderSkill =
                                                    ((skill.name === 'gear1' || skill.name === 'combo') &&
                                                        (filterState.filterBy.includes(skill.name) || filterState.filterBy.length === 0)) ||
                                                    ((skill.name === 'gear2' || skill.name === 'combo') &&
                                                        (filterState.filterBy.includes(skill.name) || filterState.filterBy.length === 0) &&
                                                        character.gear2) ||
                                                    ((filterState.filterBy.includes(skill.name) || filterState.filterBy.length === 0) &&
                                                        filterState.awakening[skill.awakening] &&
                                                        character?.[skill.name]?.[skill.awakening]);

                                                const skillContent = shouldRenderSkill
                                                    ? renderSkill(character, skill.name, skill.key, skill.awakening, skill.title, buffData)
                                                    : null;

                                                return skillContent ? (
                                                    <React.Fragment key={customKey}>
                                                        {skillContent}
                                                    </React.Fragment>
                                                ) : null;
                                            })}
                                        </div>
                                    </div >
                                    <br />
                                </div>
                            ))}
                        <div className="bottom-controls full">
                            <PageControls pageContent={filteredCharacters} page={page} handlePageChange={handlePageChange} />
                            {Object.keys(filteredCharacters).length > 5 && <div className="bottom-contorls-right"><ScrollArrow /></div>}
                        </div>
                    </div>

                </div>
                <div className="surround-margin"></div>
            </div>
        </div >
    );
}

export default SkillsPage;